import {
	FlowNode,
	PatientTreatmentPlanStageLog,
	TreatmentPlanSectionInfo,
} from "../../../../../../types/healthcare/TreatmentPlanTypes";

export const SECTION_GENERAL_KEY = "__general__";

export interface StageGroupItem {
	num: number;
	nodes: FlowNode[];
	/**
	 * The specific stage log this group renders (per-loop-attempt rendering, F2). When a stage has
	 * looped, each attempt becomes its own group carrying its own log; future (not-yet-reached) nodes
	 * have none. Section bucketing/trimming still key off `nodes[0]`, so this is purely the row's data.
	 */
	log?: PatientTreatmentPlanStageLog;
}

export interface SectionBucket {
	section: TreatmentPlanSectionInfo | null;
	panelKey: string;
	groups: StageGroupItem[];
}

export function buildSectionKeyByStageKey(
	logs: PatientTreatmentPlanStageLog[],
): Map<string, string | null> {
	const m = new Map<string, string | null>();
	logs.forEach((log) => {
		const sk = log.stage_snapshot?.stage_key ?? log.stage?.stage_key;
		if (sk) m.set(sk, log.stage_snapshot?.section_key ?? null);
	});
	return m;
}

export function extractSectionsFromCanvas(
	canvas: { nodes: FlowNode[]; edges: any[] } | null | undefined,
): TreatmentPlanSectionInfo[] {
	if (!canvas) return [];
	return canvas.nodes
		.filter((n) => n.type === "section_group")
		.sort((a, b) => {
			const dy = a.position.y - b.position.y;
			return dy !== 0 ? dy : a.position.x - b.position.x;
		})
		.map((n, idx) => ({
			section_key: ((n.data as any).section_key as string) || `section_${n.id}`,
			name: ((n.data as any).label as string) || "Section",
			color: ((n.data as any).color as string) || "#4A90D9",
			sort_order: idx,
		}))
		.filter((s) => !!s.section_key);
}

export function nearestGroupNode(
	nx: number,
	ny: number,
	groupNodes: FlowNode[],
): FlowNode | null {
	if (groupNodes.length === 0) return null;
	let best: FlowNode | null = null;
	let bestDist = Infinity;
	for (const g of groupNodes) {
		const gx = g.position.x;
		const gy = g.position.y;
		const gw = parseFloat(String((g as any).measured?.width ?? (g as any).width ?? (g as any).style?.width ?? 400));
		const gh = parseFloat(String((g as any).measured?.height ?? (g as any).height ?? (g as any).style?.height ?? 300));
		if (nx >= gx && nx <= gx + gw && ny >= gy && ny <= gy + gh) return g;
		const dx = Math.max(gx - nx, 0, nx - (gx + gw));
		const dy = Math.max(gy - ny, 0, ny - (gy + gh));
		const dist = dx * dx + dy * dy;
		if (dist < bestDist) {
			bestDist = dist;
			best = g;
		}
	}
	return best;
}

export function buildSectionKeyMapFromCanvas(
	canvas: { nodes: FlowNode[]; edges: any[] } | null | undefined,
	stageKeys: Set<string>,
): Map<string, string | null> {
	const result = new Map<string, string | null>();
	if (!canvas) return result;

	const groupNodes = canvas.nodes.filter((n) => n.type === "section_group");
	if (groupNodes.length === 0) return result;

	const groupById = new Map<string, FlowNode>(groupNodes.map((g) => [g.id, g]));

	canvas.nodes
		.filter((n) => n.type !== "section_group" && stageKeys.has(n.id))
		.forEach((n) => {
			if ((n as any).parentId) {
				const parent = groupById.get((n as any).parentId);
				if (parent) {
					result.set(n.id, ((parent.data as any).section_key as string) ?? null);
					return;
				}
			}
			const match = nearestGroupNode(n.position.x, n.position.y, groupNodes);
			result.set(n.id, match ? (((match.data as any).section_key as string) ?? null) : null);
		});

	return result;
}

/**
 * True when a node is a decision point (practitioner_option) the practitioner has NOT yet
 * resolved — i.e. its stage log has no recorded `option_<key>` outcome.
 */
export function isUndecidedDecisionNode(
	node: FlowNode,
	logByKey: Map<string, PatientTreatmentPlanStageLog>,
): boolean {
	if (node.data?.stage_type !== "practitioner_option") return false;
	const outcome = logByKey.get(node.id)?.outcome;
	return !(typeof outcome === "string" && outcome.startsWith("option_"));
}

/**
 * Section-local decision trim for the middle column: within EACH section, hide the FUTURE stages
 * that come after the section's first UNDECIDED decision point (the decision row itself stays). This
 * is scoped per section so a decision never hides later section groups — other sections render in
 * full. Once the decision is made the node is no longer undecided, so its (chosen-branch) stages
 * reappear.
 *
 * `isLogged` protects stages the plan has already REACHED: in a loop (e.g. the CoA cancel-retry
 * cycle) a reached stage can sort after an undecided decision, but it must still show — only
 * unreached/future stages past the decision are hidden.
 */
export function trimBucketsAfterUndecidedDecision(
	buckets: SectionBucket[],
	isUndecided: (node: FlowNode) => boolean,
	isLogged: (node: FlowNode) => boolean = () => false,
): SectionBucket[] {
	return buckets.map((bucket) => {
		const groups: StageGroupItem[] = [];
		let cut = false;
		for (const group of bucket.groups) {
			const node = group.nodes[0];
			if (cut) {
				// After an undecided decision, only keep stages the plan has actually reached.
				if (node && isLogged(node)) groups.push(group);
				continue;
			}
			groups.push(group);
			if (node && isUndecided(node)) cut = true; // include the decision row, then start trimming
		}
		return { ...bucket, groups };
	});
}

/**
 * Ensure every section group from the canvas is represented in the middle column — even sections
 * whose stages are all currently hidden (e.g. gated behind an undecided decision) — so the section
 * outline always shows. Missing sections are appended as empty buckets and the whole list is
 * re-sorted by section sort_order (General last).
 */
export function ensureAllSections(
	buckets: SectionBucket[],
	sections: TreatmentPlanSectionInfo[],
): SectionBucket[] {
	const present = new Set(buckets.map((b) => b.panelKey));
	const extras: SectionBucket[] = sections
		.filter((s) => !present.has(s.section_key))
		.map((s) => ({ section: s, panelKey: s.section_key, groups: [] }));

	const all = [...buckets, ...extras];
	all.sort((a, b) => {
		const ao = a.section?.sort_order ?? Number.MAX_SAFE_INTEGER;
		const bo = b.section?.sort_order ?? Number.MAX_SAFE_INTEGER;
		if (ao !== bo) return ao - bo;
		if (a.panelKey === SECTION_GENERAL_KEY) return 1;
		if (b.panelKey === SECTION_GENERAL_KEY) return -1;
		return 0;
	});
	return all;
}

export function groupStagesBySection(
	stageGroups: StageGroupItem[],
	sections: TreatmentPlanSectionInfo[],
	sectionKeyByStageKey: Map<string, string | null>,
): SectionBucket[] {
	const sectionMap = new Map<string, TreatmentPlanSectionInfo>(
		sections.map((s) => [s.section_key, s]),
	);
	const bucketMap = new Map<string, SectionBucket>();
	const ordered: string[] = [];

	stageGroups.forEach((group) => {
		const representativeKey = group.nodes[0]?.id ?? null;
		const rawSectionKey = representativeKey
			? (sectionKeyByStageKey.get(representativeKey) ?? null)
			: null;
		const panelKey = rawSectionKey ?? SECTION_GENERAL_KEY;

		if (!bucketMap.has(panelKey)) {
			bucketMap.set(panelKey, {
				section: rawSectionKey ? (sectionMap.get(rawSectionKey) ?? null) : null,
				panelKey,
				groups: [],
			});
			ordered.push(panelKey);
		}
		bucketMap.get(panelKey)!.groups.push(group);
	});

	const withOrder = ordered.map((k) => bucketMap.get(k)!);
	withOrder.sort((a, b) => {
		const ao = a.section?.sort_order ?? Number.MAX_SAFE_INTEGER;
		const bo = b.section?.sort_order ?? Number.MAX_SAFE_INTEGER;
		if (ao !== bo) return ao - bo;
		if (a.panelKey === SECTION_GENERAL_KEY) return 1;
		if (b.panelKey === SECTION_GENERAL_KEY) return -1;
		return 0;
	});

	return withOrder;
}
